import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import useAdminAuth from '../hooks/useAdminAuth';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ConfirmModal from '../components/ConfirmModal';
import { Plus, Trash2, Edit2, Package, MapPin, Calendar, DollarSign, ChevronDown, ChevronUp, MessageSquare, Camera, Image, CheckCircle, XCircle, Clock, Archive, RotateCcw, Share2, Copy, Search, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLoads = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getAdminEmail } = useAdminAuth();
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loadToDelete, setLoadToDelete] = useState(null);
  const [editingLoad, setEditingLoad] = useState(null);
  const [expandedQuotes, setExpandedQuotes] = useState({});
  const [loadQuotes, setLoadQuotes] = useState({});
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [editLoadPhotos, setEditLoadPhotos] = useState([]);
  const [changeRequests, setChangeRequests] = useState([]);
  const [showChangeRequests, setShowChangeRequests] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [archivedLoads, setArchivedLoads] = useState([]);
  const [loadingArchived, setLoadingArchived] = useState(false);
  const [socialPostData, setSocialPostData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [archivedStatusFilter, setArchivedStatusFilter] = useState('all');
  const adminEmail = getAdminEmail();
  const [formData, setFormData] = useState({
    shipper_email: '',
    contact_phone: '',
    company_name: '',
    pickup_location: '',
    dropoff_location: '',
    cargo_type: '',
    cargo_description: '',
    cargo_value: '',
    weight: '',
    dimensions: '',
    oversize_ground_contact_dimensions: '',
    oversize_extremity_dimensions: '',
    pickup_date: '',
    delivery_date: '',
    pickup_time_window: '',
    delivery_time_window: '',
    preferred_carrier_type: '',
    insurance_requirements: '',
    loading_equipment_needed: '',
    unloading_equipment_needed: '',
    additional_contact_person: '',
    additional_contact_phone: '',
    special_requirements: '',
    load_description: ''
  });

  useEffect(() => {
    fetchLoads();
    fetchChangeRequests();
  }, []);

  useEffect(() => {
    if (activeTab === 'archived' && archivedLoads.length === 0) {
      fetchArchivedLoads();
    }
  }, [activeTab]);

  const fetchLoads = async () => {
    try {
      // Admin should see unmasked data - pass admin email for verification
      const response = await axios.get(`${API}/loads`, {
        params: {
          mask_for_carrier: false,
          admin_email: adminEmail
        }
      });
      setLoads(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load jobs',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedLoads = async () => {
    setLoadingArchived(true);
    try {
      const response = await axios.get(`${API}/admin/loads/archived`);
      setArchivedLoads(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load archived jobs', variant: 'destructive' });
    } finally {
      setLoadingArchived(false);
    }
  };

  const handleRestoreLoad = async (loadId) => {
    try {
      await axios.put(`${API}/admin/loads/${loadId}/restore`);
      toast({ title: 'Success', description: 'Load restored to active' });
      fetchArchivedLoads();
      fetchLoads();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to restore load', variant: 'destructive' });
    }
  };

  const toggleQuotes = async (loadId) => {
    if (expandedQuotes[loadId]) {
      setExpandedQuotes(prev => ({ ...prev, [loadId]: false }));
      return;
    }
    try {
      const response = await axios.get(`${API}/quotes/load/${loadId}?include_pending=true&reveal_carrier=true`);
      setLoadQuotes(prev => ({ ...prev, [loadId]: response.data }));
      setExpandedQuotes(prev => ({ ...prev, [loadId]: true }));
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load quotes', variant: 'destructive' });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddLoad = async (e) => {
    e.preventDefault();
    
    if (!formData.shipper_email || !formData.pickup_location || !formData.dropoff_location) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      // Clean data for Pydantic validation - convert empty strings to null for numeric fields
      const cleanedData = { ...formData };
      if (cleanedData.weight === '') cleanedData.weight = null;
      if (cleanedData.cargo_value === '') cleanedData.cargo_value = null;
      
      await axios.post(`${API}/loads`, cleanedData);
      
      toast({
        title: 'Success',
        description: 'Load added successfully'
      });
      
      setShowAddForm(false);
      setFormData({
        shipper_email: '',
        pickup_location: '',
        dropoff_location: '',
        cargo_type: '',
        weight: '',
        dimensions: '',
        delivery_date: '',
        budget: '',
        load_description: '',
        contact_name: '',
        contact_phone: ''
      });
      
      fetchLoads();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to add load',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLoad = async (loadId) => {
    setLoadToDelete(loadId);
    setShowDeleteModal(true);
  };

  const confirmDeleteLoad = async () => {
    if (!loadToDelete) return;
    
    setLoading(true);
    try {
      await axios.delete(`${API}/loads/${loadToDelete}`);
      
      toast({
        title: 'Success',
        description: 'Load deleted successfully'
      });
      
      setShowDeleteModal(false);
      setLoadToDelete(null);
      fetchLoads();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete load',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };


  const handleCopySocialPost = async (load) => {
    try {
      const res = await axios.get(`${API}/loads/${load.id}/social-post`);
      setSocialPostData(res.data);
    } catch (error) {
      // Fallback: generate locally
      const post = `NEW LOAD AVAILABLE\n\nRoute: ${load.pickup_location} to ${load.dropoff_location}\nCargo: ${load.cargo_type}${load.weight ? `\nWeight: ${load.weight}` : ''}${load.pickup_date ? `\nAvailable From: ${new Date(load.pickup_date).toLocaleDateString('en-AU')}` : ''}\n\nQuote now: https://www.trucknsendit.com/load/${load.id}\n\n#TrucknSendIt #FreightAustralia #TruckingJobs #LoadBoard #Transport`;
      setSocialPostData({
        social_post: post,
        load_url: `https://www.trucknsendit.com/load/${load.id}`,
        has_photos: (load.load_photos || []).length > 0,
        photo_count: (load.load_photos || []).length,
        photos: load.load_photos || []
      });
    }
  };

  const copyPostText = async () => {
    if (!socialPostData) return;
    try {
      await navigator.clipboard.writeText(socialPostData.social_post);
      toast({ title: 'Copied!', description: 'Post text copied to clipboard.' });
    } catch {
      prompt('Copy this post:', socialPostData.social_post);
    }
  };

  const downloadPhoto = (dataUrl, index) => {
    const link = document.createElement('a');
    link.href = dataUrl;
    const ext = dataUrl.startsWith('data:image/png') ? 'png' : 'jpg';
    link.download = `load_photo_${index + 1}.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAllPhotos = () => {
    if (!socialPostData?.photos) return;
    socialPostData.photos.forEach((photo, i) => {
      setTimeout(() => downloadPhoto(photo, i), i * 300);
    });
    toast({ title: 'Downloading', description: `${socialPostData.photos.length} photo(s) downloading...` });
  };

  const handleEditLoad = (load) => {
    setEditingLoad(load);
    setFormData({
      shipper_email: load.shipper_email || '',
      contact_phone: load.contact_phone || '',
      company_name: load.company_name || '',
      pickup_location: load.pickup_location || '',
      dropoff_location: load.dropoff_location || '',
      cargo_type: load.cargo_type || '',
      cargo_description: load.cargo_description || '',
      cargo_value: load.cargo_value || '',
      weight: load.weight || '',
      dimensions: load.dimensions || '',
      oversize_ground_contact_dimensions: load.oversize_ground_contact_dimensions || '',
      oversize_extremity_dimensions: load.oversize_extremity_dimensions || '',
      pickup_date: load.pickup_date || '',
      delivery_date: load.delivery_date || '',
      pickup_time_window: load.pickup_time_window || '',
      delivery_time_window: load.delivery_time_window || '',
      preferred_carrier_type: load.preferred_carrier_type || '',
      insurance_requirements: load.insurance_requirements || '',
      loading_equipment_needed: load.loading_equipment_needed || '',
      unloading_equipment_needed: load.unloading_equipment_needed || '',
      additional_contact_person: load.additional_contact_person || '',
      additional_contact_phone: load.additional_contact_phone || '',
      special_requirements: load.special_requirements || '',
      load_description: load.load_description || ''
    });
    setEditLoadPhotos(load.load_photos || []);
    setShowEditModal(true);
  };

  const handleUpdateLoad = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.put(`${API}/loads/${editingLoad.id}`, formData);
      
      toast({
        title: 'Success',
        description: 'Load updated successfully'
      });
      
      setShowEditModal(false);
      setEditingLoad(null);
      fetchLoads();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update load',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingPhotos(true);
    try {
      const photoPromises = files.map(file => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });
      
      const base64Photos = await Promise.all(photoPromises);
      
      await axios.put(`${API}/admin/loads/${editingLoad.id}/photos`, {
        photos: base64Photos
      });
      
      setEditLoadPhotos(prev => [...prev, ...base64Photos]);
      toast({ title: 'Success', description: `${files.length} photo(s) uploaded` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to upload photos', variant: 'destructive' });
    } finally {
      setUploadingPhotos(false);
      e.target.value = '';
    }
  };

  const handleDeletePhoto = async (index) => {
    try {
      await axios.delete(`${API}/admin/loads/${editingLoad.id}/photos/${index}`);
      setEditLoadPhotos(prev => prev.filter((_, i) => i !== index));
      toast({ title: 'Success', description: 'Photo removed' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete photo', variant: 'destructive' });
    }
  };

  const fetchChangeRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/load-change-requests?status=pending`);
      setChangeRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch change requests:', error);
    }
  };

  const handleApproveChangeRequest = async (requestId) => {
    try {
      await axios.put(`${API}/admin/load-change-requests/${requestId}/approve`, { admin_email: adminEmail });
      toast({ title: 'Approved', description: 'Changes applied to load' });
      fetchChangeRequests();
      fetchLoads();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to approve', variant: 'destructive' });
    }
  };

  const handleRejectChangeRequest = async (requestId) => {
    try {
      await axios.put(`${API}/admin/load-change-requests/${requestId}/reject`, { 
        admin_email: adminEmail, 
        rejection_reason: rejectReason 
      });
      toast({ title: 'Rejected', description: 'Change request rejected' });
      setRejectReason('');
      fetchChangeRequests();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to reject', variant: 'destructive' });
    }
  };


  const cargoTypes = [
    'General Freight', 'Heavy Haulage', 'Livestock Transport', 'Machinery Transport',
    'Container Transport', 'Car Transport', 'Boat Transport', 'Grain Transport',
    'Hay Transport', 'Liquid Tanker', 'Palletised Freight', 'Hot Shot', 'Pilot Job'
  ];

  // Case-insensitive search across common load fields
  const matchesSearch = (load) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    const haystack = [
      load.id,
      load.pickup_location,
      load.dropoff_location,
      load.cargo_type,
      load.cargo_description,
      load.shipper_email,
      load.company_name,
      load.contact_phone,
      load.additional_contact_person,
      load.additional_contact_phone,
      load.load_description,
      load.special_requirements,
      load.status,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  };

  const visibleActiveLoads = loads.filter(matchesSearch);
  const filteredArchived = archivedLoads.filter((l) => {
    if (archivedStatusFilter === 'all') return matchesSearch(l);
    // 'completed' groups delivered loads (business term)
    if (archivedStatusFilter === 'completed') return l.status === 'delivered' && matchesSearch(l);
    return l.status === archivedStatusFilter && matchesSearch(l);
  });

  const archivedCounts = archivedLoads.reduce((acc, l) => {
    acc.all += 1;
    if (l.status === 'delivered') acc.completed += 1;
    else if (l.status === 'cancelled') acc.cancelled += 1;
    else if (l.status === 'expired') acc.expired += 1;
    else if (l.status === 'deleted') acc.deleted += 1;
    return acc;
  }, { all: 0, completed: 0, cancelled: 0, expired: 0, deleted: 0 });

  if (loading && loads.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <p>Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 sm:px-6 py-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#1e3a5f]">Manage Loads</h1>
              <p className="text-gray-600 mt-2">Add, edit, and delete loads as admin</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => navigate('/admin')}
                variant="outline"
                className="border-gray-300"
              >
                Back to Dashboard
              </Button>
              <Button
                onClick={() => setShowAddForm(!showAddForm)}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add New Load
              </Button>
            </div>
          </div>

          {/* Admin Email Display */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <Label>Logged in as:</Label>
            <p className="text-sm text-gray-600 mt-1">{adminEmail}</p>
          </div>

          {/* Add Load Form */}
          {showAddForm && (
            <Card className="p-6 mb-8">
              <h2 className="text-xl font-bold text-[#1e3a5f] mb-6">Add New Load</h2>
              
              <form onSubmit={handleAddLoad} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="shipper_email">Shipper Email *</Label>
                    <Input
                      id="shipper_email"
                      name="shipper_email"
                      type="email"
                      value={formData.shipper_email}
                      onChange={handleChange}
                      placeholder="shipper@example.com"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="contact_name">Contact Name</Label>
                    <Input
                      id="contact_name"
                      name="contact_name"
                      value={formData.contact_name}
                      onChange={handleChange}
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <Label htmlFor="contact_phone">Contact Phone</Label>
                    <Input
                      id="contact_phone"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleChange}
                      placeholder="04XX XXX XXX"
                    />
                  </div>

                  <div>
                    <Label htmlFor="pickup_location">Pickup Location (Suburb, State) *</Label>
                    <Input
                      id="pickup_location"
                      name="pickup_location"
                      value={formData.pickup_location}
                      onChange={handleChange}
                      placeholder="e.g. Parramatta, NSW"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="dropoff_location">Dropoff Location (Suburb, State) *</Label>
                    <Input
                      id="dropoff_location"
                      name="dropoff_location"
                      value={formData.dropoff_location}
                      onChange={handleChange}
                      placeholder="e.g. Port Lincoln, SA"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="cargo_type">Cargo Type</Label>
                    <select
                      id="cargo_type"
                      name="cargo_type"
                      value={formData.cargo_type}
                      onChange={handleChange}
                      className="w-full h-10 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      <option value="">Select cargo type...</option>
                      {cargoTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="weight">Weight (kg)</Label>
                    <Input
                      id="weight"
                      name="weight"
                      type="number"
                      value={formData.weight}
                      onChange={handleChange}
                      placeholder="e.g., 5000"
                    />
                  </div>

                  <div>
                    <Label htmlFor="dimensions">Dimensions (L x W x H)</Label>
                    <Input
                      id="dimensions"
                      name="dimensions"
                      value={formData.dimensions}
                      onChange={handleChange}
                      placeholder="e.g., 6m x 2.5m x 3m"
                    />
                  </div>

                  <div>
                    <Label htmlFor="delivery_date">Delivery Date</Label>
                    <Input
                      id="delivery_date"
                      name="delivery_date"
                      type="date"
                      value={formData.delivery_date}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <Label htmlFor="budget">Budget ($)</Label>
                    <Input
                      id="budget"
                      name="budget"
                      type="number"
                      value={formData.budget}
                      onChange={handleChange}
                      placeholder="e.g., 2500"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="load_description">Load Description</Label>
                  <Textarea
                    id="load_description"
                    name="load_description"
                    value={formData.load_description}
                    onChange={handleChange}
                    placeholder="Describe the load details..."
                    rows={4}
                  />
                </div>

                <div className="flex gap-3">
                  <Button 
                    type="submit"
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {loading ? 'Adding...' : 'Add Load'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          )}


          {/* Edit Load Modal */}
          {showEditModal && editingLoad && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
              <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 my-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-[#1e3a5f]">Edit Load</h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingLoad(null);
                    }}
                  >
                    ✕
                  </Button>
                </div>
                
                <form onSubmit={handleUpdateLoad} className="space-y-6">
                  {/* Contact Information */}
                  <div className="border-b pb-4">
                    <h3 className="font-semibold text-gray-700 mb-4">Contact Information</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="edit_shipper_email">Shipper Email *</Label>
                        <Input
                          id="edit_shipper_email"
                          name="shipper_email"
                          type="email"
                          value={formData.shipper_email}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_contact_phone">Contact Phone</Label>
                        <Input
                          id="edit_contact_phone"
                          name="contact_phone"
                          value={formData.contact_phone}
                          onChange={handleChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_company_name">Company Name</Label>
                        <Input
                          id="edit_company_name"
                          name="company_name"
                          value={formData.company_name}
                          onChange={handleChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_additional_contact_person">Additional Contact Person</Label>
                        <Input
                          id="edit_additional_contact_person"
                          name="additional_contact_person"
                          value={formData.additional_contact_person}
                          onChange={handleChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_additional_contact_phone">Additional Contact Phone</Label>
                        <Input
                          id="edit_additional_contact_phone"
                          name="additional_contact_phone"
                          value={formData.additional_contact_phone}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Location Information */}
                  <div className="border-b pb-4">
                    <h3 className="font-semibold text-gray-700 mb-4">Location & Route</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="edit_pickup_location">Pickup Location (Suburb, State) *</Label>
                        <Input
                          id="edit_pickup_location"
                          name="pickup_location"
                          value={formData.pickup_location}
                          onChange={handleChange}
                          placeholder="e.g. Parramatta, NSW"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_dropoff_location">Dropoff Location (Suburb, State) *</Label>
                        <Input
                          id="edit_dropoff_location"
                          name="dropoff_location"
                          value={formData.dropoff_location}
                          onChange={handleChange}
                          placeholder="e.g. Port Lincoln, SA"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Cargo Information */}
                  <div className="border-b pb-4">
                    <h3 className="font-semibold text-gray-700 mb-4">Cargo Details</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="edit_cargo_type">Cargo Type *</Label>
                        <Input
                          id="edit_cargo_type"
                          name="cargo_type"
                          value={formData.cargo_type}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_weight">Weight (kg)</Label>
                        <Input
                          id="edit_weight"
                          name="weight"
                          type="number"
                          value={formData.weight}
                          onChange={handleChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_dimensions">Dimensions</Label>
                        <Input
                          id="edit_dimensions"
                          name="dimensions"
                          value={formData.dimensions}
                          onChange={handleChange}
                          placeholder="e.g., 6m x 2.5m x 3m"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_cargo_value">Cargo Value ($)</Label>
                        <Input
                          id="edit_cargo_value"
                          name="cargo_value"
                          type="number"
                          value={formData.cargo_value}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label htmlFor="edit_cargo_description">Cargo Description</Label>
                        <Textarea
                          id="edit_cargo_description"
                          name="cargo_description"
                          value={formData.cargo_description}
                          onChange={handleChange}
                          rows={3}
                        />
                      </div>
                      
                      {/* Oversize Load Fields */}
                      <div className="md:col-span-2">
                        <Label htmlFor="edit_oversize_ground_contact_dimensions">Ground Contact Dimensions (Oversize Loads)</Label>
                        <Input
                          id="edit_oversize_ground_contact_dimensions"
                          name="oversize_ground_contact_dimensions"
                          value={formData.oversize_ground_contact_dimensions || ''}
                          onChange={handleChange}
                          placeholder="e.g., 3.5m wide, 12m long"
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <Label htmlFor="edit_oversize_extremity_dimensions">Outermost Extremity Dimensions (Oversize Loads)</Label>
                        <Input
                          id="edit_oversize_extremity_dimensions"
                          name="oversize_extremity_dimensions"
                          value={formData.oversize_extremity_dimensions || ''}
                          onChange={handleChange}
                          placeholder="e.g., 15m x 4.5m x 4.2m"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Dates & Times */}
                  <div className="border-b pb-4">
                    <h3 className="font-semibold text-gray-700 mb-4">Schedule</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="edit_pickup_date">Available From</Label>
                        <Input
                          id="edit_pickup_date"
                          name="pickup_date"
                          type="date"
                          value={formData.pickup_date}
                          onChange={handleChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_delivery_date">Delivery Date</Label>
                        <Input
                          id="edit_delivery_date"
                          name="delivery_date"
                          type="date"
                          value={formData.delivery_date}
                          onChange={handleChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_pickup_time_window">Pickup Time Window</Label>
                        <Input
                          id="edit_pickup_time_window"
                          name="pickup_time_window"
                          value={formData.pickup_time_window}
                          onChange={handleChange}
                          placeholder="e.g., 8am - 12pm"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_delivery_time_window">Delivery Time Window</Label>
                        <Input
                          id="edit_delivery_time_window"
                          name="delivery_time_window"
                          value={formData.delivery_time_window}
                          onChange={handleChange}
                          placeholder="e.g., 1pm - 5pm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Requirements */}
                  <div className="border-b pb-4">
                    <h3 className="font-semibold text-gray-700 mb-4">Requirements & Preferences</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="edit_preferred_carrier_type">Preferred Carrier Type</Label>
                        <Input
                          id="edit_preferred_carrier_type"
                          name="preferred_carrier_type"
                          value={formData.preferred_carrier_type}
                          onChange={handleChange}
                          placeholder="e.g., owner-operator, fleet"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_insurance_requirements">Insurance Requirements</Label>
                        <Input
                          id="edit_insurance_requirements"
                          name="insurance_requirements"
                          value={formData.insurance_requirements}
                          onChange={handleChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_loading_equipment_needed">Loading Equipment Needed</Label>
                        <Input
                          id="edit_loading_equipment_needed"
                          name="loading_equipment_needed"
                          value={formData.loading_equipment_needed}
                          onChange={handleChange}
                          placeholder="e.g., forklift, crane"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_unloading_equipment_needed">Unloading Equipment Needed</Label>
                        <Input
                          id="edit_unloading_equipment_needed"
                          name="unloading_equipment_needed"
                          value={formData.unloading_equipment_needed}
                          onChange={handleChange}
                          placeholder="e.g., forklift, crane"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-4">Additional Information</h3>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="edit_special_requirements">Special Requirements</Label>
                        <Textarea
                          id="edit_special_requirements"
                          name="special_requirements"
                          value={formData.special_requirements}
                          onChange={handleChange}
                          rows={3}
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_load_description">Load Description</Label>
                        <Textarea
                          id="edit_load_description"
                          name="load_description"
                          value={formData.load_description}
                          onChange={handleChange}
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Load Photos */}
                  <div className="border-t pt-4">
                    <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                      <Camera className="w-5 h-5" />
                      Load Photos
                    </h3>
                    
                    {editLoadPhotos.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        {editLoadPhotos.map((photo, index) => (
                          <div key={index} className="relative group rounded-lg overflow-hidden border">
                            <img src={photo} alt={`Load photo ${index + 1}`} className="w-full h-32 object-cover" />
                            <button
                              type="button"
                              onClick={() => handleDeletePhoto(index)}
                              className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              data-testid={`delete-photo-${index}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <label className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors" data-testid="upload-load-photos">
                      <Image className="w-5 h-5 text-gray-500" />
                      <span className="text-sm text-gray-600">
                        {uploadingPhotos ? 'Uploading...' : 'Click to upload photos'}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handlePhotoUpload}
                        disabled={uploadingPhotos}
                        className="hidden"
                      />
                    </label>
                  </div>

                  <div className="flex gap-3 pt-4 border-t">
                    <Button 
                      type="submit"
                      disabled={loading}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {loading ? 'Updating...' : 'Update Load'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowEditModal(false);
                        setEditingLoad(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card>
            </div>
          )}


          {/* Pending Change Requests */}
          {changeRequests.length > 0 && (
            <Card className="p-6 border-2 border-amber-300 bg-amber-50 mb-6" data-testid="change-requests-panel">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-amber-800 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Pending Change Requests ({changeRequests.length})
                </h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowChangeRequests(!showChangeRequests)}
                >
                  {showChangeRequests ? 'Hide' : 'Show'}
                </Button>
              </div>
              
              {showChangeRequests && (
                <div className="space-y-4">
                  {changeRequests.map((req) => (
                    <div key={req.id} className="bg-white rounded-lg p-4 border border-amber-200" data-testid={`change-request-${req.id}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-semibold text-gray-800">
                            Load: {req.load_info?.cargo_type || 'N/A'} — {req.load_info?.pickup_location} → {req.load_info?.dropoff_location}
                          </p>
                          <p className="text-sm text-gray-500">From: {req.shipper_email} | {new Date(req.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      
                      {req.reason && (
                        <p className="text-sm text-gray-600 mb-3 italic">Reason: {req.reason}</p>
                      )}
                      
                      <div className="bg-gray-50 rounded p-3 mb-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Proposed Changes:</p>
                        {Object.entries(req.proposed_changes).map(([field, value]) => (
                          <div key={field} className="flex gap-2 text-sm mb-1">
                            <span className="font-medium text-gray-700 min-w-[140px]">{field.replace(/_/g, ' ')}:</span>
                            <span className="text-gray-900">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handleApproveChangeRequest(req.id)}
                          data-testid={`approve-change-${req.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Input
                          placeholder="Rejection reason (optional)"
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          className="flex-1 text-sm h-9"
                        />
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleRejectChangeRequest(req.id)}
                          data-testid={`reject-change-${req.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}

          {/* Loads List */}
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by pickup, dropoff, cargo type, shipper email, company, load ID..."
                className="pl-10 pr-10 h-11 bg-white"
                data-testid="admin-loads-search"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="admin-loads-search-clear"
                  title="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Active / Archived Tabs */}
            <div className="flex items-center gap-2 border-b border-gray-200 mb-4">
              <button
                onClick={() => setActiveTab('active')}
                className={`px-4 py-2.5 font-semibold text-sm border-b-2 transition-colors ${
                  activeTab === 'active' 
                    ? 'border-[#1e3a5f] text-[#1e3a5f]' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                data-testid="active-loads-tab"
              >
                Active Loads ({searchTerm ? `${visibleActiveLoads.length}/${loads.length}` : loads.length})
              </button>
              <button
                onClick={() => setActiveTab('archived')}
                className={`px-4 py-2.5 font-semibold text-sm border-b-2 transition-colors flex items-center gap-1.5 ${
                  activeTab === 'archived' 
                    ? 'border-[#1e3a5f] text-[#1e3a5f]' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                data-testid="archived-loads-tab"
              >
                <Archive className="w-4 h-4" />
                Archived / Completed ({archivedLoads.length})
              </button>
            </div>

            {activeTab === 'active' && (<>
            {loads.length === 0 ? (
              <Card className="p-12 text-center">
                <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No loads found</p>
                <Button
                  onClick={() => setShowAddForm(true)}
                  className="mt-4 bg-green-600 hover:bg-green-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Load
                </Button>
              </Card>
            ) : visibleActiveLoads.length === 0 ? (
              <Card className="p-12 text-center" data-testid="admin-loads-empty-search">
                <Search className="w-14 h-14 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">No active loads match "{searchTerm}"</p>
                <Button variant="outline" onClick={() => setSearchTerm('')} className="mt-4">
                  Clear search
                </Button>
              </Card>
            ) : (
              visibleActiveLoads.map((load) => (
                <Card key={load.id} className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                          {load.cargo_type || 'General Freight'}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          load.status === 'open' ? 'bg-green-100 text-green-800' :
                          load.status === 'in_transit' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {load.status || 'open'}
                        </span>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-5 h-5 text-green-600 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-600">Pickup</p>
                            <p className="font-medium text-gray-900">{load.pickup_location}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <MapPin className="w-5 h-5 text-red-600 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-600">Dropoff</p>
                            <p className="font-medium text-gray-900">{load.dropoff_location}</p>
                          </div>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-3 gap-4 text-sm mb-3">
                        {load.weight && (
                          <div>
                            <p className="text-gray-600">Weight</p>
                            <p className="font-medium">{load.weight} kg</p>
                          </div>
                        )}
                        {load.dimensions && (
                          <div>
                            <p className="text-gray-600">Dimensions</p>
                            <p className="font-medium">{load.dimensions}</p>
                          </div>
                        )}
                        {load.oversize_ground_contact_dimensions && (
                          <div>
                            <p className="text-gray-600">Ground Contact (Oversize)</p>
                            <p className="font-medium">{load.oversize_ground_contact_dimensions}</p>
                          </div>
                        )}
                        {load.oversize_extremity_dimensions && (
                          <div>
                            <p className="text-gray-600">Extremity Dimensions (Oversize)</p>
                            <p className="font-medium">{load.oversize_extremity_dimensions}</p>
                          </div>
                        )}
                        {load.cargo_value && (
                          <div>
                            <p className="text-gray-600">Cargo Value</p>
                            <p className="font-medium">${load.cargo_value.toLocaleString()}</p>
                          </div>
                        )}
                        {load.pickup_date && (
                          <div>
                            <p className="text-gray-600">Available From</p>
                            <p className="font-medium">{new Date(load.pickup_date).toLocaleDateString()}</p>
                          </div>
                        )}
                        {load.delivery_date && (
                          <div>
                            <p className="text-gray-600">Delivery Date</p>
                            <p className="font-medium">{new Date(load.delivery_date).toLocaleDateString()}</p>
                          </div>
                        )}
                        {load.budget && (
                          <div>
                            <p className="text-gray-600">Budget</p>
                            <p className="font-medium">${load.budget.toLocaleString()}</p>
                          </div>
                        )}
                        {load.pickup_time_window && (
                          <div>
                            <p className="text-gray-600">Pickup Time Window</p>
                            <p className="font-medium">{load.pickup_time_window}</p>
                          </div>
                        )}
                        {load.delivery_time_window && (
                          <div>
                            <p className="text-gray-600">Delivery Time Window</p>
                            <p className="font-medium">{load.delivery_time_window}</p>
                          </div>
                        )}
                        {load.preferred_carrier_type && (
                          <div>
                            <p className="text-gray-600">Preferred Carrier Type</p>
                            <p className="font-medium capitalize">{load.preferred_carrier_type.replace(/_/g, ' ')}</p>
                          </div>
                        )}
                      </div>

                      {/* Additional Details Section */}
                      {(load.cargo_description || load.insurance_requirements || load.loading_equipment_needed || 
                        load.unloading_equipment_needed || load.special_requirements) && (
                        <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                          {load.cargo_description && (
                            <div>
                              <p className="text-xs text-gray-600 font-semibold">Cargo Description:</p>
                              <p className="text-sm text-gray-800">{load.cargo_description}</p>
                            </div>
                          )}
                          {load.insurance_requirements && (
                            <div>
                              <p className="text-xs text-gray-600 font-semibold">Insurance Requirements:</p>
                              <p className="text-sm text-gray-800">{load.insurance_requirements}</p>
                            </div>
                          )}
                          {load.loading_equipment_needed && (
                            <div>
                              <p className="text-xs text-gray-600 font-semibold">Loading Equipment Needed:</p>
                              <p className="text-sm text-gray-800">{load.loading_equipment_needed}</p>
                            </div>
                          )}
                          {load.unloading_equipment_needed && (
                            <div>
                              <p className="text-xs text-gray-600 font-semibold">Unloading Equipment Needed:</p>
                              <p className="text-sm text-gray-800">{load.unloading_equipment_needed}</p>
                            </div>
                          )}
                          {load.special_requirements && (
                            <div>
                              <p className="text-xs text-gray-600 font-semibold">Special Requirements:</p>
                              <p className="text-sm text-gray-800">{load.special_requirements}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {load.load_description && (
                        <div className="mt-3">
                          <p className="text-xs text-gray-600 font-semibold">Load Description:</p>
                          <p className="text-sm text-gray-800 mt-1">{load.load_description}</p>
                        </div>
                      )}

                      {/* Freight Image */}
                      {load.freight_image && (
                        <div className="mt-3">
                          <p className="text-xs text-gray-600 font-semibold mb-2">Freight Image:</p>
                          <img 
                            src={load.freight_image} 
                            alt="Freight" 
                            className="max-w-xs rounded border cursor-pointer hover:opacity-80"
                            onClick={() => window.open(load.freight_image, '_blank')}
                          />
                        </div>
                      )}

                      {/* Shipper Contact Information (Admin View) */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Shipper Contact:</p>
                        <div className="grid md:grid-cols-3 gap-3 text-sm">
                          {load.shipper_email && (
                            <div>
                              <p className="text-gray-600">Email</p>
                              <p className="font-medium">{load.shipper_email}</p>
                            </div>
                          )}
                          {load.contact_phone && (
                            <div>
                              <p className="text-gray-600">Phone</p>
                              <p className="font-medium">{load.contact_phone}</p>
                            </div>
                          )}
                          {load.company_name && (
                            <div>
                              <p className="text-gray-600">Company</p>
                              <p className="font-medium">{load.company_name}</p>
                            </div>
                          )}
                          {load.additional_contact_person && (
                            <div>
                              <p className="text-gray-600">Additional Contact</p>
                              <p className="font-medium">{load.additional_contact_person}</p>
                            </div>
                          )}
                          {load.additional_contact_phone && (
                            <div>
                              <p className="text-gray-600">Additional Phone</p>
                              <p className="font-medium">{load.additional_contact_phone}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      <p className="text-xs text-gray-500 mt-3">
                        Load ID: {load.id} | Posted: {new Date(load.created_at).toLocaleString()}
                      </p>

                      {/* Quotes Section */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <button
                          onClick={() => toggleQuotes(load.id)}
                          className="flex items-center gap-2 text-sm font-semibold text-[#1e3a5f] hover:text-blue-700 transition-colors"
                        >
                          <MessageSquare className="w-4 h-4" />
                          Quotes Received
                          {expandedQuotes[load.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        
                        {expandedQuotes[load.id] && (
                          <div className="mt-3 space-y-2">
                            {(!loadQuotes[load.id] || loadQuotes[load.id].length === 0) ? (
                              <p className="text-sm text-gray-500 italic">No quotes received yet.</p>
                            ) : (
                              loadQuotes[load.id].map((quote) => (
                                <div key={quote.id} className="bg-gray-50 border rounded-lg p-3 text-sm">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <p className="font-semibold text-gray-900">{quote.carrier_name || quote.carrier_email}</p>
                                      {quote.carrier_company && <p className="text-xs text-gray-500">{quote.carrier_company}</p>}
                                    </div>
                                    <div className="text-right">
                                      <p className="font-bold text-green-700">${quote.amount?.toLocaleString()}</p>
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                                        quote.status === 'approved' ? 'bg-green-100 text-green-800' :
                                        quote.status === 'accepted' ? 'bg-blue-100 text-blue-800' :
                                        quote.status === 'pending_approval' ? 'bg-yellow-100 text-yellow-800' :
                                        quote.status === 'declined' ? 'bg-red-100 text-red-800' :
                                        'bg-gray-100 text-gray-800'
                                      }`}>
                                        {quote.status?.replace(/_/g, ' ')}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex gap-4 mt-2 text-xs text-gray-600">
                                    <span>Est. delivery: {quote.estimated_delivery_days} day{quote.estimated_delivery_days !== 1 ? 's' : ''}</span>
                                    {quote.demurrage_rate && <span>Demurrage: ${quote.demurrage_rate}/hr</span>}
                                    {quote.quote_valid_days && <span>Valid: {quote.quote_valid_days} day{quote.quote_valid_days !== 1 ? 's' : ''}</span>}
                                  </div>
                                  {quote.message && <p className="mt-1 text-xs text-gray-600 italic">"{quote.message}"</p>}
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopySocialPost(load)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-300"
                        title="Copy social media post"
                        data-testid={`social-post-${load.id}`}
                      >
                        <Share2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditLoad(load)}
                        title="Edit Load"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteLoad(load.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300"
                        title="Delete Load"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}
            </>)}

            {activeTab === 'archived' && (
              <div className="space-y-4">
                {/* Status filter chips */}
                {!loadingArchived && archivedLoads.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2" data-testid="archived-status-filters">
                    {[
                      { key: 'all', label: 'All', count: archivedCounts.all, activeClass: 'bg-[#1e3a5f] text-white border-[#1e3a5f]' },
                      { key: 'completed', label: 'Completed', count: archivedCounts.completed, activeClass: 'bg-green-600 text-white border-green-600' },
                      { key: 'cancelled', label: 'Cancelled', count: archivedCounts.cancelled, activeClass: 'bg-red-600 text-white border-red-600' },
                      { key: 'expired', label: 'Expired', count: archivedCounts.expired, activeClass: 'bg-yellow-600 text-white border-yellow-600' },
                      { key: 'deleted', label: 'Deleted', count: archivedCounts.deleted, activeClass: 'bg-gray-700 text-white border-gray-700' },
                    ].map(chip => {
                      const isActive = archivedStatusFilter === chip.key;
                      return (
                        <button
                          key={chip.key}
                          type="button"
                          onClick={() => setArchivedStatusFilter(chip.key)}
                          className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${
                            isActive ? chip.activeClass : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                          }`}
                          data-testid={`archived-filter-${chip.key}`}
                        >
                          {chip.label} <span className={isActive ? 'opacity-90' : 'text-gray-500'}>({chip.count})</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {loadingArchived ? (
                  <Card className="p-12 text-center">
                    <p className="text-gray-500">Loading archived loads...</p>
                  </Card>
                ) : archivedLoads.length === 0 ? (
                  <Card className="p-12 text-center">
                    <Archive className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No archived loads</p>
                    <p className="text-sm text-gray-400 mt-1">Completed, cancelled, expired and deleted loads will appear here</p>
                  </Card>
                ) : filteredArchived.length === 0 ? (
                  <Card className="p-12 text-center" data-testid="admin-archived-empty">
                    <Search className="w-14 h-14 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">
                      {searchTerm ? `No ${archivedStatusFilter === 'all' ? 'archived' : archivedStatusFilter} loads match "${searchTerm}"` : `No ${archivedStatusFilter} loads`}
                    </p>
                    <div className="flex justify-center gap-2 mt-4">
                      {searchTerm && (
                        <Button variant="outline" onClick={() => setSearchTerm('')}>Clear search</Button>
                      )}
                      {archivedStatusFilter !== 'all' && (
                        <Button variant="outline" onClick={() => setArchivedStatusFilter('all')}>Show all</Button>
                      )}
                    </div>
                  </Card>
                ) : (
                  filteredArchived.map((load) => (
                    <Card key={load.id} className="p-4 sm:p-5 border border-gray-200 bg-gray-50 opacity-80" data-testid={`archived-load-${load.id}`}>
                      <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h3 className="text-lg font-semibold text-gray-700">{load.cargo_type}</h3>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              load.status === 'delivered' ? 'bg-green-100 text-green-800' :
                              load.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                              load.status === 'expired' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-200 text-gray-700'
                            }`}>
                              {load.status.charAt(0).toUpperCase() + load.status.slice(1)}
                            </span>
                            {load.quotes_received > 0 && (
                              <span className="text-xs text-gray-500">{load.quotes_received} quotes</span>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
                            <div className="flex items-center gap-1.5">
                              <MapPin className="w-3.5 h-3.5 text-green-600" />
                              <span>{load.pickup_location}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <MapPin className="w-3.5 h-3.5 text-red-600" />
                              <span>{load.dropoff_location}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5" />
                              <span>Posted: {new Date(load.created_at).toLocaleDateString()}</span>
                            </div>
                            {load.shipper_email && (
                              <div className="text-xs text-gray-500">Shipper: {load.shipper_email}</div>
                            )}
                          </div>
                          
                          <p className="text-xs text-gray-400 mt-2">ID: {load.id}</p>
                        </div>
                        
                        <div className="flex gap-2">
                          {load.status !== 'delivered' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRestoreLoad(load.id)}
                              className="text-green-600 hover:bg-green-50 border-green-300"
                              data-testid={`restore-load-${load.id}`}
                            >
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Restore
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setLoadToDelete(null);
        }}
        onConfirm={confirmDeleteLoad}
        title="Archive Load"
        message="Are you sure you want to archive this load? It will move to the Archived tab and can be restored later."
        confirmText="Archive Load"
        variant="danger"
        loading={loading}
      />

      {/* Social Media Post Modal */}
      {socialPostData && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b bg-[#1e3a5f] text-white rounded-t-xl">
              <h2 className="text-lg font-bold flex items-center gap-2"><Share2 className="w-5 h-5" /> Social Media Post</h2>
              <button onClick={() => setSocialPostData(null)} className="hover:bg-white/20 rounded-full p-1">
                <span className="text-xl leading-none">&times;</span>
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Post Text */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-gray-700">Post Text</p>
                  <Button size="sm" onClick={copyPostText} className="bg-[#1e3a5f] hover:bg-[#15304f] text-white" data-testid="copy-social-post">
                    <Copy className="w-3.5 h-3.5 mr-1" /> Copy Text
                  </Button>
                </div>
                <pre className="bg-gray-50 border rounded-lg p-3 text-sm whitespace-pre-wrap font-sans text-gray-800">{socialPostData.social_post}</pre>
              </div>

              {/* Photos */}
              {socialPostData.has_photos && socialPostData.photos?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-gray-700">Freight Photos ({socialPostData.photo_count})</p>
                    <Button size="sm" variant="outline" onClick={downloadAllPhotos} data-testid="download-all-photos">
                      <Image className="w-3.5 h-3.5 mr-1" /> Download All
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {socialPostData.photos.map((photo, i) => (
                      <div key={i} className="relative group cursor-pointer" onClick={() => downloadPhoto(photo, i)}>
                        <img src={photo} alt={`Load photo ${i + 1}`} className="w-full h-24 object-cover rounded-lg border" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                          <span className="text-white text-xs font-medium">Download</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Click a photo to download, or use "Download All" to save them together. Attach to your social media post.</p>
                </div>
              )}

              {!socialPostData.has_photos && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                  No photos attached to this load. Upload photos via the Edit button to include them in social posts.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default AdminLoads;
